// Seed data for Café Breizh Purchasing Operations
import { ROLES } from "../constants";

const IMG = (base, w = 600) =>
  base.includes("?")
    ? `${base}&w=${w}&auto=format&fit=crop&q=70`
    : `${base}?w=${w}&auto=format&fit=crop&q=70`;

export const SEED_USERS = [
  {
    id: "u-main",
    name: "Julio",
    email: "julio@cafebreizh.com",
    role: ROLES.MAIN,
    areas: [],
    restaurant: null,
    pin: "8811",
    active: true,
  },
  {
    id: "u-r1-mgr",
    name: "Marc Leroux",
    email: "marc@cafebreizh.com",
    role: ROLES.RESTAURANT,
    areas: [],
    restaurant: "restaurant_1",
    pin: "2802",
    active: true,
  },
  {
    id: "u-r2-mgr",
    name: "Céline Rouvier",
    email: "celine@cafebreizh.com",
    role: ROLES.RESTAURANT,
    areas: [],
    restaurant: "restaurant_2",
    pin: "2107",
    active: true,
  },
];

// Legacy demo employee user IDs that used to ship with the app.
// They are removed from the seed list above and actively scrubbed on every
// device by `purgeLegacyDemoUsers()` in `lib/sync.js`. Listed here so the
// migration knows exactly which IDs to delete, even after a fresh install.
export const LEGACY_DEMO_USER_IDS = [
  "u-r1-kitchen",
  "u-r1-coffee",
  "u-r1-cleaning",
  "u-r1-bakery",
  "u-r2-pastry",
  "u-r2-bakery",
  "u-r2-kitchen",
];

export const SEED_SUPPLIERS = [
  { id: "sup-mpg", name: "MPG", type: "delivered", lead: "2 days" },
  { id: "sup-sams", name: "Sam's Club", type: "direct", lead: "Same day pickup" },
  { id: "sup-jw", name: "JW Food Distribution", type: "delivered", lead: "Next day" },
  { id: "sup-chef", name: "Chef Warehouse", type: "delivered", lead: "Next day" },
  { id: "sup-bake", name: "Bake Mark", type: "delivered", lead: "Tue / Fri" },
  { id: "sup-usf", name: "US Food", type: "delivered", lead: "2 days" },
  { id: "sup-internal", name: "Internal Transportation", type: "direct", lead: "Between locations" },
];

export const SEED_CATEGORIES = [
  { id: "cat-dairy", name: "Dairy", order: 0 },
  { id: "cat-produce", name: "Produce", order: 1 },
  { id: "cat-bakery", name: "Bakery", order: 2 },
  { id: "cat-dry-goods", name: "Dry Goods", order: 3 },
  { id: "cat-beverages", name: "Beverages", order: 4 },
  { id: "cat-paper", name: "Paper & Disposables", order: 5 },
  { id: "cat-cleaning", name: "Cleaning", order: 6 },
  { id: "cat-meat-deli", name: "Meat & Deli", order: 7 },
];

const now = new Date().toISOString();

const baseProduct = (p) => ({
  assignedAreas: p.assignedAreas || [],
  assignedRestaurants: p.assignedRestaurants || ["restaurant_1", "restaurant_2"],
  availableForPurchase: p.availableForPurchase ?? true,
  availableForTransfer: p.availableForTransfer ?? true,
  active: p.active ?? true,
  ownerRestaurant: p.ownerRestaurant || "global",
  createdAt: p.createdAt || now,
  updatedAt: p.updatedAt || now,
  ...p,
});

export const SEED_PRODUCTS = [
  baseProduct({ id: "p-001", name: "Whole Milk, 1 gal", category: "Dairy", unit: "gal", supplierId: "sup-jw", par: 12, assignedAreas: ["kitchen", "coffee_bar", "pastry", "bakery"], image: IMG("https://images.unsplash.com/photo-1553301803-768cd4a59b9c") }),
  baseProduct({ id: "p-002", name: "Heavy Cream, qt", category: "Dairy", unit: "qt", supplierId: "sup-jw", par: 8, assignedAreas: ["kitchen", "coffee_bar", "pastry"], image: IMG("https://images.unsplash.com/photo-1637194502327-c99c94943680") }),
  baseProduct({ id: "p-003", name: "Salted Butter, 1 lb", category: "Dairy", unit: "lb", supplierId: "sup-jw", par: 20, assignedAreas: ["bakery", "pastry", "kitchen"], image: IMG("https://images.pexels.com/photos/12162627/pexels-photo-12162627.jpeg") }),
  baseProduct({ id: "p-004", name: "Eggs, large, dozen", category: "Dairy", unit: "dz", supplierId: "sup-usf", par: 30, assignedAreas: ["kitchen", "pastry", "bakery"], image: IMG("https://images.unsplash.com/photo-1634821507330-b7df872692c2") }),
  baseProduct({ id: "p-005", name: "Baby Spinach, case", category: "Produce", unit: "case", supplierId: "sup-chef", par: 4, assignedAreas: ["kitchen"], image: IMG("https://images.unsplash.com/photo-1576045057995-568f588f82fb") }),
  baseProduct({ id: "p-006", name: "Tomatoes on the vine, lb", category: "Produce", unit: "lb", supplierId: "sup-chef", par: 25, assignedAreas: ["kitchen"], image: IMG("https://images.unsplash.com/photo-1582284540020-8acbe03f4924") }),
  baseProduct({ id: "p-007", name: "Avocado, hass, each", category: "Produce", unit: "ea", supplierId: "sup-chef", par: 40, assignedAreas: ["kitchen"], image: IMG("https://images.unsplash.com/photo-1523049673857-eb18f1d7b578") }),
  baseProduct({ id: "p-008", name: "Bread Flour, 50 lb", category: "Dry Goods", unit: "bag", supplierId: "sup-bake", par: 6, assignedAreas: ["bakery", "pastry"], image: IMG("https://images.unsplash.com/photo-1761222191837-4448599c09fc") }),
  baseProduct({ id: "p-009", name: "Granulated Sugar, 25 lb", category: "Dry Goods", unit: "bag", supplierId: "sup-bake", par: 4, assignedAreas: ["bakery", "pastry", "coffee_bar"], image: IMG("https://images.unsplash.com/photo-1673791031093-eb8eefa60083") }),
  baseProduct({ id: "p-010", name: "Sea Salt, fine, 3 lb", category: "Dry Goods", unit: "tub", supplierId: "sup-usf", par: 3, assignedAreas: ["kitchen", "bakery", "pastry"], image: IMG("https://images.pexels.com/photos/2624397/pexels-photo-2624397.jpeg") }),
  baseProduct({ id: "p-011", name: "Croissant dough, frozen", category: "Bakery", unit: "case", supplierId: "sup-bake", par: 5, assignedAreas: ["bakery", "pastry"], image: IMG("https://images.unsplash.com/photo-1605345746984-8ade72b44e00") }),
  baseProduct({ id: "p-012", name: "Pain au chocolat, batons", category: "Bakery", unit: "box", supplierId: "sup-bake", par: 3, assignedAreas: ["bakery", "pastry"], image: IMG("https://images.pexels.com/photos/31310834/pexels-photo-31310834.jpeg") }),
  baseProduct({ id: "p-013", name: "Espresso beans, Breizh blend", category: "Beverages", unit: "lb", supplierId: "sup-usf", par: 16, assignedAreas: ["coffee_bar"], image: IMG("https://images.unsplash.com/photo-1580933073521-dc49ac0d4e6a") }),
  baseProduct({ id: "p-014", name: "Oat milk, 32 oz", category: "Beverages", unit: "carton", supplierId: "sup-usf", par: 18, assignedAreas: ["coffee_bar"], image: IMG("https://images.unsplash.com/photo-1607292819104-c54624be6bc2") }),
  baseProduct({ id: "p-015", name: "12oz paper cups", category: "Paper & Disposables", unit: "sleeve", supplierId: "sup-sams", par: 8, assignedAreas: ["coffee_bar", "kitchen"], image: IMG("https://images.pexels.com/photos/15476690/pexels-photo-15476690.jpeg") }),
  baseProduct({ id: "p-016", name: "Cup lids, hot", category: "Paper & Disposables", unit: "sleeve", supplierId: "sup-sams", par: 8, assignedAreas: ["coffee_bar"], image: IMG("https://images.pexels.com/photos/15476690/pexels-photo-15476690.jpeg") }),
  baseProduct({ id: "p-017", name: "Kraft napkins, 500 ct", category: "Paper & Disposables", unit: "pack", supplierId: "sup-sams", par: 6, assignedAreas: ["coffee_bar", "kitchen"], image: IMG("https://images.unsplash.com/photo-1580428456190-7348f304b927") }),
  baseProduct({ id: "p-018", name: "All-purpose cleaner, gal", category: "Cleaning", unit: "gal", supplierId: "sup-sams", par: 2, assignedAreas: ["cleaning", "kitchen"], image: IMG("https://images.unsplash.com/photo-1550963295-019d8a8a61c5") }),
  baseProduct({ id: "p-019", name: "Nitrile gloves, M, case", category: "Cleaning", unit: "case", supplierId: "sup-sams", par: 2, assignedAreas: ["cleaning", "kitchen", "bakery", "pastry"], image: IMG("https://images.unsplash.com/photo-1599412227383-b7d4751c8765") }),
  baseProduct({ id: "p-020", name: "Smoked ham, sliced, lb", category: "Meat & Deli", unit: "lb", supplierId: "sup-mpg", par: 10, assignedAreas: ["kitchen"], image: IMG("https://images.unsplash.com/photo-1609604820237-4cd9638aa43e") }),
  baseProduct({ id: "p-021", name: "Gruyère, wheel", category: "Meat & Deli", unit: "ea", supplierId: "sup-mpg", par: 2, assignedAreas: ["kitchen"], image: IMG("https://images.unsplash.com/photo-1634925353703-451b16b6d1a8") }),
  baseProduct({ id: "p-022", name: "Baguettes, par-baked", category: "Bakery", unit: "case", supplierId: "sup-bake", par: 6, assignedAreas: ["bakery", "kitchen"], image: IMG("https://images.unsplash.com/photo-1691862469586-9c7c183b27b7") }),
];

// Demo requests removed: they referenced legacy demo employees that no
// longer ship with the app. Fresh devices start with an empty request
// queue and the real workspace data syncs in via `lib/sync.js`.
export const SEED_REQUESTS = [];

export const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_stock-request-hub-3/artifacts/7jh4ksgs_IMG_3949.webp";

export const HERO_BACKDROP_IMAGE =
  "https://customer-assets.emergentagent.com/job_stock-request-hub-3/artifacts/hmhi9ara_DSC09035.webp";
